import { AccountItem } from '@pango-inc/keybox-sdk-wrapper/dist/sdk-wrapper.main';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { isServiceAvailable, parseJSON } from 'utils';
import { orderBy, sortBy } from 'lodash';
import { DIGITAL_VAULT } from 'utils/constants';
import { LooseObjectType } from 'utils/globalTypes';
import { SERVICES } from 'utils/service-entitlements-config';
import {
  selectEntitlements,
  selectIsUserAgent,
  selectUserFeatures,
} from 'services/session/sessionSelectors';
import DOCUMENT_ITEM_TYPES, {
  DocumentItemKeyType,
} from 'pages/vault/digital-vault/partials/select-document-item-type/data';
import { DocumentItemType } from './types';

// Selectors
export const selectVault = (state: RootState) => state.vault;
export const selectAppInfo = createSelector(
  selectVault,
  (vault) => vault.info.data,
);

// select vault accounts
export const selectVaultAccounts = createSelector(
  [selectVault],
  ({ accounts }) =>
    accounts?.list.length > 1
      ? orderBy(accounts.list, ['updatedAt', 'createdAt'], ['desc', 'desc'])
      : accounts.list,
);

// select vault accounts sorted by account name
export const selectSortedAccountsByAccountName = createSelector(
  [selectVault],
  ({ accounts }) =>
    accounts?.list.length > 1
      ? sortBy(accounts.list, (account) => account.title.toLowerCase())
      : accounts.list,
);

// select isUserAuthrised
export const selectIsUserAuthorized = createSelector(
  [selectAppInfo],
  (data) => data?.isAuthorized,
);

// select isVaultLocked
export const selectIsVaultLocked = createSelector(
  selectAppInfo,
  (data) => data?.isLocked,
);

// select Vault Exists
export const selectIsVaultExists = createSelector(
  selectVault,
  (vault) => vault.isVaultExists,
);

// select account by id
export const selectAccountById = createSelector(
  selectVaultAccounts,
  (_, id) => id,
  (accounts, id: string) =>
    accounts.find((account: AccountItem) => account.id === id) ?? null,
);

export const selectVaultDocuments = createSelector(selectVault, (vault) => {
  const sortedDocuments = sortBy(vault.documents, (doc) =>
    doc.title.trim().toLowerCase(),
  );

  return sortedDocuments.map((document) => {
    const typeName = (Object.keys(DOCUMENT_ITEM_TYPES).find((typeKey) => {
      const key = typeKey as DocumentItemKeyType;
      return DOCUMENT_ITEM_TYPES[key].id === document.type;
    }) || DIGITAL_VAULT.fallbackDocumentType) as DocumentItemKeyType;

    return {
      ...document,
      typeName,
      payload: parseJSON(document.payload, '{}'),
    };
  });
});

export const selectVaultDocumentsCount = createSelector(selectVault, (vault) =>
  Array.isArray(vault.documents) ? vault.documents.length : 0,
);

export const selectVaultDocumentsFileSize = createSelector(
  selectVault,
  (vault) => {
    if (!Array.isArray(vault.documents)) {
      return 0;
    }

    return vault.documents.reduce((acc, document) => {
      if (!Array.isArray(document.fileInfos)) {
        return acc;
      }

      const documentFileSize = document.fileInfos.reduce(
        (fileAcc, fileInfo: LooseObjectType) => {
          return fileAcc + fileInfo.size;
        },
        0,
      );
      return acc + documentFileSize;
    }, 0);
  },
);

export const selectVaultDocumentsById = createSelector(
  selectVaultDocuments,
  (_, id) => id,
  (documents, id: string): null | DocumentItemType => {
    if (!id) {
      return null;
    }
    return (
      documents.find((document: DocumentItemType) => document.id === id) ?? null
    );
  },
);

// select is vault loaded
export const selectIsVaultLoaded = createSelector(
  selectVault,
  (vault) => vault.isLoading,
);

// select whether the user is entitled to the vault (passwords + digital vault)
export const selectHasVaultAccess = createSelector(
  [selectUserFeatures, selectIsUserAgent, selectEntitlements],
  (userFeatures, isAgent, rawEntitlements) =>
    isServiceAvailable(SERVICES.digitalVault, {
      userFeatures,
      isAgent,
      rawEntitlements,
    }),
);

// select show password defense card to install
export const selectShowPasswordDefenseInstallCard = createSelector(
  selectVault,
  (vault) => vault?.showPasswordDefenseInstallcard,
);
